import Taro from '@tarojs/taro';
import { DEFAULT_SETTINGS, GameSettings, normalizeSettings } from '@/data/settings';
import { BOARD_HEIGHT, BOARD_WIDTH, CARD_SIZE, BoardMode } from '@/data/board';
import { FIRST_PRACTICE_LEVEL } from '@/data/levels';
import { GameState, checkInvariants, createIdleState, effectiveElapsedMs, quizGrantUsage } from './gameEngine';
import { checkCover } from './game';
import { reserveGroupsOf, reserveColumnsOf } from './staging';

export interface GameStats {
  highScore: number; totalWins: number; longestWinStreak: number; currentWinStreak: number;
  appliedRunIds?: string[]; [key: string]: unknown;
}
export interface LevelProgress {
  maxUnlockedLevel: number; completedLevels: number[]; bestTimes: Record<number, number>; [key: string]: unknown;
}
// These legacy keys are deliberately stable across all updates.
const STORAGE_KEY = 'dog_game_stats';
const LEVEL_PROGRESS_KEY = 'dog_game_level_progress';
const SETTINGS_KEY = 'dog_game_settings';
const ACTIVE_KEY = 'sheep_active_run_v1';
const MODE_KEY = 'sheep_mode_progress_v1';
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const positive = (value: unknown, fallback = 0) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback;
const parse = (value: unknown): Record<string, unknown> | null => {
  try { const data = typeof value === 'string' ? JSON.parse(value) : value; return object(data) ? data : null; }
  catch { return null; }
};
const readRaw = (key: string) => { try { return parse(Taro.getStorageSync(key)); } catch { return null; } };
const read = (key: string): Record<string, unknown> | null => {
  return readRaw(key) || readRaw(key + '_backup');
};
/** Keep a previous readable copy before replacing a record. Never clear all storage. */
const write = (key: string, data: unknown): boolean => {
  try {
    const previous = parse(Taro.getStorageSync(key));
    const encoded = JSON.stringify(data);
    Taro.setStorageSync(key + '_backup', previous ? JSON.stringify(previous) : encoded);
    Taro.setStorageSync(key, encoded);
    return true;
  } catch (error) { console.error('保存失败：' + key, error); return false; }
};
const statsOf = (data: Record<string, unknown> | null): GameStats => ({
  ...data, highScore: positive(data?.highScore), totalWins: positive(data?.totalWins),
  longestWinStreak: positive(data?.longestWinStreak), currentWinStreak: positive(data?.currentWinStreak),
  appliedRunIds: Array.isArray(data?.appliedRunIds) ? data!.appliedRunIds.filter(id => typeof id === 'string') : []
});
const progressOf = (data: Record<string, unknown> | null): LevelProgress => {
  const completedLevels: number[] = Array.isArray(data?.completedLevels)
    ? Array.from(new Set(data!.completedLevels.filter(level => typeof level === 'number' && Number.isInteger(level) && level > 0))) : [];
  const bestTimes: Record<number, number> = {};
  if (object(data?.bestTimes)) Object.entries(data!.bestTimes).forEach(([level, time]) => {
    if (Number.isInteger(Number(level)) && Number(level) > 0 && typeof time === 'number' && Number.isFinite(time) && time >= 0) bestTimes[Number(level)] = time;
  });
  return { ...data, maxUnlockedLevel: Math.max(1, positive(data?.maxUnlockedLevel, 1)), completedLevels, bestTimes };
};
export const getSettings = (): GameSettings => normalizeSettings(read(SETTINGS_KEY) || DEFAULT_SETTINGS);
export const saveSettings = (settings: GameSettings): boolean => write(SETTINGS_KEY, normalizeSettings(settings));
export const toggleSound = () => { const s = getSettings(); s.soundEnabled = !s.soundEnabled; saveSettings(s); return s; };
export const toggleMusic = () => { const s = getSettings(); s.musicEnabled = !s.musicEnabled; saveSettings(s); return s; };
export const getGameStats = (): GameStats => statsOf(read(STORAGE_KEY));
export const saveGameStats = (stats: GameStats) => write(STORAGE_KEY, statsOf(stats));
export const recordWin = (score: number, runId?: string) => {
  const stats = getGameStats();
  if (runId && stats.appliedRunIds?.includes(runId)) return true;
  stats.highScore = Math.max(stats.highScore, positive(score));
  stats.totalWins++; stats.currentWinStreak++;
  stats.longestWinStreak = Math.max(stats.longestWinStreak, stats.currentWinStreak);
  if (runId) stats.appliedRunIds = [...(stats.appliedRunIds || []), runId];
  return saveGameStats(stats);
};
export const recordLoss = (runId?: string) => {
  const stats = getGameStats();
  if (runId && stats.appliedRunIds?.includes(runId)) return true;
  stats.currentWinStreak = 0;
  if (runId) stats.appliedRunIds = [...(stats.appliedRunIds || []), runId];
  return saveGameStats(stats);
};
export const resetStats = () => saveGameStats(statsOf(null));
export const getLevelProgress = (): LevelProgress => progressOf(read(LEVEL_PROGRESS_KEY));
export const saveLevelProgress = (progress: LevelProgress) => write(LEVEL_PROGRESS_KEY, progressOf(progress));
export const completeLevel = (level: number, timeInSeconds?: number) => {
  const progress = getLevelProgress();
  if (!progress.completedLevels.includes(level)) progress.completedLevels.push(level);
  progress.maxUnlockedLevel = Math.max(progress.maxUnlockedLevel, Math.min(level + 1, 4));
  if (typeof timeInSeconds === 'number' && Number.isFinite(timeInSeconds) && timeInSeconds >= 0) {
    progress.bestTimes[level] = Math.min(progress.bestTimes[level] ?? Infinity, timeInSeconds);
  }
  return saveLevelProgress(progress);
};
export const getBestTime = (level: number) => getLevelProgress().bestTimes[level];
export const getStartLevel = () => Math.max(FIRST_PRACTICE_LEVEL, Math.min(4, getLevelProgress().maxUnlockedLevel));
export const formatTime = (seconds: number): string => {
  const value = positive(seconds);
  return Math.floor(value / 60).toString().padStart(2, '0') + ':' + (value % 60).toString().padStart(2, '0');
};
export const isLevelUnlocked = (level: number) => level >= FIRST_PRACTICE_LEVEL && level <= Math.max(FIRST_PRACTICE_LEVEL, getLevelProgress().maxUnlockedLevel);
export const isLevelCompleted = (level: number) => getLevelProgress().completedLevels.includes(level);

export interface ModeProgress {
  dailyDates: string[]; dailyBest: Record<string, number>; worldCompleted: number[];
  worldStage: number; worldBest: Record<string, number>; appliedRunIds: string[];
}
const modeProgressOf = (saved: Record<string, unknown> | null): ModeProgress => {
  const strings = (value: unknown) => Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  const times = (value: unknown): Record<string, number> => {
    const result: Record<string, number> = {};
    if (object(value)) Object.entries(value).forEach(([key, time]) => {
      if (typeof time === 'number' && Number.isFinite(time) && time >= 0) result[key] = time;
    });
    return result;
  };
  return { dailyDates: strings(saved?.dailyDates), dailyBest: times(saved?.dailyBest), worldBest: times(saved?.worldBest),
    worldCompleted: Array.isArray(saved?.worldCompleted) ? saved!.worldCompleted.filter(v => typeof v === 'number' && Number.isInteger(v) && v >= 1) : [],
    worldStage: Math.max(1, positive(saved?.worldStage, 1)), appliedRunIds: strings(saved?.appliedRunIds) };
};
export const getModeProgress = (): ModeProgress => modeProgressOf(read(MODE_KEY));
export const recordModeWin = (mode: BoardMode, key: string, stage: number, seconds: number, runId: string): boolean => {
  const progress = getModeProgress();
  if (progress.appliedRunIds.includes(runId)) return true;
  if (mode === 'daily') {
    if (!progress.dailyDates.includes(key)) progress.dailyDates.push(key);
    progress.dailyBest[key] = Math.min(progress.dailyBest[key] ?? Infinity, seconds);
  } else {
    if (!progress.worldCompleted.includes(stage)) progress.worldCompleted.push(stage);
    progress.worldStage = Math.max(progress.worldStage, stage + 1);
    progress.worldBest[stage] = Math.min(progress.worldBest[stage] ?? Infinity, seconds);
  }
  progress.appliedRunIds.push(runId);
  return write(MODE_KEY, progress);
};

const restoreRun = (envelope: Record<string, unknown> | null): GameState | null => {
  const raw = envelope?.state;
  if (!object(raw) || typeof raw.runId !== 'string' || !raw.runId ||
    typeof raw.level !== 'number' || !Number.isInteger(raw.level) || raw.level < 1 || raw.level > 4 ||
    typeof raw.phase !== 'string' || !['playing', 'paused', 'failed', 'quiz', 'won'].includes(raw.phase) ||
    !Array.isArray(raw.cards) || !raw.cards.length || raw.cards.length > 500 ||
    !Array.isArray(raw.slotCardIds) || !Array.isArray(raw.reserveCardIds)) return null;
  const saved = raw as unknown as GameState;
  const cards = saved.cards;
  if (cards.some(card => !object(card) || typeof card.id !== 'string' || typeof card.type !== 'string' ||
    ![0, 1, 2, 3].includes(card.status) || ![card.x, card.y, card.zIndex].every(value => typeof value === 'number' && Number.isFinite(value)) ||
    (card.size !== undefined && (typeof card.size !== 'number' || !Number.isFinite(card.size) || card.size <= 0 || card.size > CARD_SIZE)))) return null;
  const settings = normalizeSettings(saved.settings);
  const balances = (value: unknown) => normalizeSettings({ props: value }).props;
  const decodedHistory = (Array.isArray(raw.history) ? raw.history : []).map(snapshot => {
    if (object(snapshot) && Array.isArray(snapshot.cardStatus) && snapshot.cardStatus.length === cards.length &&
      snapshot.cardStatus.every(status => [0, 1, 2, 3].includes(status))) {
      const statuses: number[] = snapshot.cardStatus;
      const fields = { ...snapshot }; delete fields.cardStatus;
      return { ...fields, cards: checkCover(cards.map((card, index) => ({ ...card, status: statuses[index] }))) };
    }
    return snapshot;
  });
  const next: GameState = {
    ...createIdleState(), ...saved, cards: cards.map(card => ({ ...card })),
    reserveGroups: reserveGroupsOf(saved.reserveCardIds, saved.reserveGroups),
    reserveColumns: reserveColumnsOf(saved.reserveCardIds, saved.reserveGroups, saved.reserveColumns),
    settings, props: balances(saved.props), initialProps: balances(saved.initialProps),
    elapsedMs: positive(saved.elapsedMs), revision: positive(saved.revision), seed: positive(saved.seed),
    phase: saved.phase === 'won' ? 'won' : saved.phase === 'failed' || (saved.phase === 'quiz' && saved.quiz?.returnPhase === 'failed') ? 'failed' : 'paused',
    lastResumeAt: null, quiz: null, failSnapshot: null,
    quizStats: { firstAttempts: positive(saved.quizStats?.firstAttempts), firstCorrect: positive(saved.quizStats?.firstCorrect),
      completed: positive(saved.quizStats?.completed), grants: positive(saved.quizStats?.grants), grantsByKind: quizGrantUsage(saved.quizStats) },
    history: decodedHistory.filter(snapshot =>
      object(snapshot) && Array.isArray(snapshot.cards) && Array.isArray(snapshot.slotCardIds) &&
      Array.isArray(snapshot.reserveCardIds) && checkInvariants({ ...saved, ...snapshot } as GameState)).slice(-10) as GameState['history']
  };
  // Future board-size changes can migrate coordinates without changing IDs, tray, or records.
  const xRatio = BOARD_WIDTH / (positive(envelope?.boardWidth, BOARD_WIDTH) || BOARD_WIDTH);
  const yRatio = BOARD_HEIGHT / (positive(envelope?.boardHeight, BOARD_HEIGHT) || BOARD_HEIGHT);
  if (xRatio !== 1 || yRatio !== 1) {
    next.cards = next.cards.map(card => ({ ...card, x: card.x * xRatio, y: card.y * yRatio }));
    next.history = [];
  }
  // Coverage is derived, never a persistent authority across rule fixes.
  next.cards = checkCover(next.cards);
  next.history = next.history.map(snapshot => ({ ...snapshot, cards: checkCover(snapshot.cards) }));
  return checkInvariants(next) ? next : null;
};
export const loadActiveRun = (): GameState | null => {
  const primary = readRaw(ACTIVE_KEY);
  // Keep an unacknowledged result after returning home, but never resurrect an ended board.
  if (primary?.ended) {
    const notice = primary.lastSettledRun;
    if (object(notice) && typeof notice.runId === 'string' && ['win', 'fail', 'abandon'].includes(String(notice.outcome)) &&
      typeof notice.level === 'number' && typeof notice.score === 'number' && typeof notice.elapsedMs === 'number') {
      return { ...createIdleState(), lastSettledRun: notice as unknown as GameState['lastSettledRun'] };
    }
    return null;
  }
  const recover = (value: Record<string, unknown> | null) => { try { return restoreRun(value); } catch { return null; } };
  const restored = recover(primary) || recover(readRaw(ACTIVE_KEY + '_backup'));
  if (restored && !restored.boardMode) {
    return { ...createIdleState(), lastSettledRun: restored.lastSettledRun };
  }
  return restored;
};
export const saveActiveRun = (state: GameState, now = Date.now()): boolean => {
  if (state.phase === 'ready') return write(ACTIVE_KEY, { ended: true, lastSettledRun: state.lastSettledRun });
  const history = state.history.map(snapshot => {
    const sameLayout = snapshot.cards.length === state.cards.length && snapshot.cards.every((card, index) => {
      const current = state.cards[index];
      return card.id === current.id && card.type === current.type && card.x === current.x && card.y === current.y && card.zIndex === current.zIndex &&
        card.size === current.size && card.sourceId === current.sourceId && card.layer === current.layer;
    });
    return sameLayout ? { ...snapshot, cards: undefined, cardStatus: snapshot.cards.map(card => card.status) } : snapshot;
  });
  return write(ACTIVE_KEY, { version: 2, boardWidth: BOARD_WIDTH, boardHeight: BOARD_HEIGHT,
    state: { ...state, history, failSnapshot: null, elapsedMs: effectiveElapsedMs(state, now), lastResumeAt: null } });
};
export const exportRecords = (): string => JSON.stringify({
  format: 'sheep-records', version: 1, exportedAt: new Date().toISOString(),
  stats: getGameStats(), progress: getLevelProgress(), settings: getSettings(), modeProgress: getModeProgress()
}, null, 2);
export const importRecords = (text: string): boolean => {
  const data = parse(text);
  if (data?.format !== 'sheep-records' || data.version !== 1 || !object(data.stats) || !object(data.progress) || !object(data.settings)) return false;
  const existing = getGameStats(), incoming = statsOf(data.stats);
  const progress = getLevelProgress(), other = progressOf(data.progress);
  const merged = { ...existing, highScore: Math.max(existing.highScore, incoming.highScore),
    totalWins: Math.max(existing.totalWins, incoming.totalWins),
    currentWinStreak: incoming.totalWins > existing.totalWins ? incoming.currentWinStreak : existing.currentWinStreak,
    longestWinStreak: Math.max(existing.longestWinStreak, incoming.longestWinStreak),
    appliedRunIds: Array.from(new Set([...(existing.appliedRunIds || []), ...(incoming.appliedRunIds || [])])) };
  other.completedLevels.forEach(level => { if (!progress.completedLevels.includes(level)) progress.completedLevels.push(level); });
  Object.entries(other.bestTimes).forEach(([level, time]) => { progress.bestTimes[Number(level)] = Math.min(progress.bestTimes[Number(level)] ?? Infinity, time); });
  progress.maxUnlockedLevel = Math.max(progress.maxUnlockedLevel, other.maxUnlockedLevel);
  if (!write('sheep_before_import', JSON.parse(exportRecords()))) return false;
  const modes = getModeProgress(), importedModes = modeProgressOf(object(data.modeProgress) ? data.modeProgress : null);
  modes.dailyDates = Array.from(new Set([...modes.dailyDates, ...importedModes.dailyDates]));
  modes.worldCompleted = Array.from(new Set([...modes.worldCompleted, ...importedModes.worldCompleted]));
  modes.worldStage = Math.max(modes.worldStage, importedModes.worldStage);
  modes.appliedRunIds = Array.from(new Set([...modes.appliedRunIds, ...importedModes.appliedRunIds]));
  for (const key of ['dailyBest', 'worldBest'] as const) Object.entries(importedModes[key]).forEach(([id, time]) => {
    modes[key][id] = Math.min(modes[key][id] ?? Infinity, time);
  });
  return saveGameStats(merged) && saveLevelProgress(progress) && saveSettings(normalizeSettings(data.settings)) && write(MODE_KEY, modes);
};
